/**
 * SettingsLayout - shared layout for settings pages
 */

import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@stores/authStore';
import { APP_VERSION } from '@/config/version';

const settingsLinks = [
  { path: '/settings/profile', labelKey: 'settings.profile' },
  { path: '/settings/account', labelKey: 'settings.account' },
  { path: '/settings/organization', labelKey: 'settings.organization' },
  { path: '/settings/members', labelKey: 'settings.members' },
  { path: '/settings/notifications', labelKey: 'settings.notifications' },
  { path: '/settings/voice', labelKey: 'voice.settings' },
  { path: '/settings/ai-prompts', labelKey: 'settings.aiPrompts' },
  { path: '/settings/custom-fields', labelKey: 'settings.customFields.title' },
  { path: '/settings/integrations', labelKey: 'settings.integrations' },
  { path: '/settings/mcp', labelKey: 'settings.mcpSettings' },
  { path: '/settings/api-tokens', labelKey: 'settings.apiTokens' },
  { path: '/settings/webhooks', labelKey: 'settings.webhooks.title' },
  { path: '/settings/subscription', labelKey: 'settings.subscription.title' },
];

const enterpriseLinks = [
  { path: '/enterprise/sso', labelKey: 'enterprise.sso' },
  { path: '/enterprise/mfa', labelKey: 'enterprise.mfa' },
  { path: '/enterprise/roles', labelKey: 'enterprise.roles' },
  { path: '/enterprise/audit', labelKey: 'enterprise.audit' },
  { path: '/enterprise/privacy', labelKey: 'enterprise.privacy' },
];

const superAdminLinks = [
  { path: '/enterprise/admin', labelKey: 'enterprise.superAdmin.dashboard', end: true },
  { path: '/enterprise/admin/users', labelKey: 'enterprise.superAdmin.users' },
  { path: '/enterprise/admin/organizations', labelKey: 'enterprise.superAdmin.organizations' },
  { path: '/enterprise/admin/usage', labelKey: 'enterprise.superAdmin.usage' },
  { path: '/enterprise/admin/subscriptions', labelKey: 'enterprise.superAdmin.subscriptions' },
];

export default function SettingsLayout() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { currentOrganization, user } = useAuthStore();

  // Check if user has admin/owner role (for enterprise features visibility)
  const isEnterpriseOrg = currentOrganization?.tier === 'business' || currentOrganization?.tier === 'enterprise';
  const isOnEnterprisePage = location.pathname.startsWith('/enterprise');
  const isSuperAdmin = user?.isSuperAdmin ?? false;

  // Determine the page title based on location
  const pageTitle = isOnEnterprisePage ? t('enterprise.title') : t('settings.title');
  const pageDescription = isOnEnterprisePage ? t('enterprise.description') : t('settings.description');

  return (
    <div className="workspace-surface min-h-screen bg-neutral-50 dark:bg-neutral-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 sm:py-8">
        <div className="mb-5 sm:mb-8">
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">
            {pageTitle}
          </h1>
          <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-400">
            {pageDescription}
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-5 lg:gap-8">
          <div className="lg:hidden">
            <label htmlFor="settings-section" className="mb-2 block text-sm font-medium text-neutral-700 dark:text-neutral-300">{t('settings.sectionLabel', 'Settings section')}</label>
            <select id="settings-section" value={location.pathname} onChange={(event) => navigate(event.target.value)} className="min-h-11 w-full rounded-lg border border-neutral-300 bg-white px-3 text-sm dark:border-neutral-700 dark:bg-neutral-900 dark:text-white">
              <optgroup label={t('settings.title')}>{settingsLinks.map((link) => <option key={link.path} value={link.path}>{t(link.labelKey)}</option>)}</optgroup>
              <optgroup label={t('enterprise.title')}>{enterpriseLinks.map((link) => <option key={link.path} value={link.path}>{t(link.labelKey)}</option>)}</optgroup>
              {isSuperAdmin && <optgroup label={t('enterprise.superAdmin.title')}>{superAdminLinks.map((link) => <option key={link.path} value={link.path}>{t(link.labelKey)}</option>)}</optgroup>}
            </select>
          </div>
          {/* Sidebar navigation */}
          <nav aria-label={t('settings.title')} className="hidden w-56 shrink-0 self-start lg:sticky lg:top-24 lg:block lg:max-h-[calc(100dvh-7rem)] lg:overflow-y-auto">
            {/* Settings section */}
            <div className="mb-6">
              <h3 className="px-4 mb-2 text-xs font-semibold text-neutral-500 uppercase tracking-wider">
                {t('settings.title')}
              </h3>
              <ul className="space-y-1">
                {settingsLinks.map((link) => (
                  <li key={link.path}>
                    <NavLink
                      to={link.path}
                      className={({ isActive }) =>
                        `block px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          isActive
                            ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400'
                            : 'text-neutral-700 hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-800'
                        }`
                      }
                    >
                      {t(link.labelKey)}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>

            {/* Enterprise section - always show for navigation but indicate tier requirement */}
            <div>
              <h3 className="px-4 mb-2 text-xs font-semibold text-neutral-500 uppercase tracking-wider flex items-center gap-2">
                {t('enterprise.title')}
                {!isEnterpriseOrg && (
                  <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 rounded">
                    {t('enterprise.businessPlus')}
                  </span>
                )}
              </h3>
              <ul className="space-y-1">
                {enterpriseLinks.map((link) => (
                  <li key={link.path}>
                    <NavLink
                      to={link.path}
                      className={({ isActive }) =>
                        `block px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          isActive
                            ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400'
                            : 'text-neutral-700 hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-800'
                        }`
                      }
                    >
                      {t(link.labelKey)}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>

            {/* Super Admin section - only visible to super admins */}
            {isSuperAdmin && (
              <div className="mt-6">
                <h3 className="px-4 mb-2 text-xs font-semibold text-neutral-500 uppercase tracking-wider flex items-center gap-2">
                  {t('enterprise.superAdmin.title')}
                  <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 rounded">
                    {t('enterprise.superAdmin.adminBadge', 'Admin')}
                  </span>
                </h3>
                <ul className="space-y-1">
                  {superAdminLinks.map((link) => (
                    <li key={link.path}>
                      <NavLink
                        to={link.path}
                        end={'end' in link ? link.end : false}
                        className={({ isActive }) =>
                          `block px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                            isActive
                              ? 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400'
                              : 'text-neutral-700 hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-800'
                          }`
                        }
                      >
                        {t(link.labelKey)}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Version info */}
            <div className="mt-8 px-4 pt-4 border-t border-neutral-200 dark:border-neutral-700">
              <p className="text-xs text-neutral-400 dark:text-neutral-500">
                NeoKanban v{APP_VERSION}
              </p>
            </div>
          </nav>

          {/* Main content */}
          <main className="min-w-0 flex-1">
            <div className="rounded-xl border border-neutral-200 bg-white shadow-sm dark:border-neutral-800 dark:bg-neutral-900">
              <Outlet />
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
